import { Component, EventEmitter, inject, Output } from '@angular/core';
import { LoadingOptions } from '../../../../../models/shared/loading-options';
import { ResetPasswordRequest } from '../../../../../models/users/requests/reset-password-request';
import { BaseComponent } from '../../../../../models/base/base-component';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PortalService } from '../../../../../services/portal/portal.service';
import { ForgotPasswordViewModel } from '../../../viewModels/forgot-password-view-model';
import { ActivatedRoute } from '@angular/router';
import { PasswordInputUtils } from '../../../../../utils/password-input-utils';
import { createResetPasswordFormGroup } from '../../../forms/reset-password-form-group';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss', '../auth.component.scss']
})
export class ResetPasswordComponent extends BaseComponent {
  route = inject(ActivatedRoute);
  viewModel = inject(ForgotPasswordViewModel);
  portalService = inject(PortalService);

  @Output() loadingOpts = new EventEmitter<LoadingOptions>();

  // View
  public _loadingOpts = new BehaviorSubject(LoadingOptions.default());
  public loadingOpts$ = this._loadingOpts as Observable<LoadingOptions>;
  public isLoading$ = this._loadingOpts.asObservable().pipe(map(opts => opts.isLoading));
  // Variables
  public formGroup = createResetPasswordFormGroup();
  public formSubmitted = false;
  public passwordHintText = PasswordInputUtils.passwordHintText;

  public customErrorMessages = {
    password: {
      passwordMinLength: $localize`Password must be at least 8 characters`,
      passwordMaxLength: $localize`Password cannot exceed 50 characters`
    }
  };

  constructor() {
    super();
  }

  setupBindings() {
    this.viewModel.loadingOpts$.subscribeWhileAlive({
      owner: this,
      next: opts => this.loadingOpts.emit(opts)
    });
  }

  // Prefill runs in setupViews (ngOnInit) so synchronous emissions patch the form before the first change detection pass
  setupViews() {
    this.viewModel.getUrlParams(this.route);
    combineLatest([this.viewModel.preFillEmail$, this.viewModel.preFillCode$]).subscribeWhileAlive({
      owner: this,
      next: ([email, code]) => {
        this.formGroup.controls.email.patchValue(email);
        this.formGroup.controls.code.patchValue(code);
      }
    });
  }

  public submit(): void {
    this.formSubmitted = true;
    if (this.formGroup.invalid) {
      return;
    }
    const { email, code, newPassword } = this.formGroup.getRawValue();
    const req = new ResetPasswordRequest(email, code);
    req.newPassword = newPassword;
    this.portalService.portalType$.once(type => {
      this.viewModel.resetPassword(req, type);
    });
  }
}
